import React from 'react';
import { Mail, Phone, Linkedin, Globe, Github, MapPin, Award, Calendar, Download, Printer } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';

const Resume = () => {
  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Action Buttons - Hidden in Print */}
      <div className="no-print fixed top-6 right-6 flex gap-3 z-50">
        <Button
          onClick={handlePrint}
          className="bg-lime-600 hover:bg-lime-700 text-white shadow-lg"
        >
          <Printer className="w-4 h-4 mr-2" />
          Print
        </Button>
        <Button
          onClick={handleDownloadPDF}
          className="bg-lime-600 hover:bg-lime-700 text-white shadow-lg"
        >
          <Download className="w-4 h-4 mr-2" />
          Download PDF
        </Button>
      </div>

      {/* Resume Container */}
      <div className="max-w-5xl mx-auto px-6 py-12">
        <Card className="bg-slate-800 border-slate-700 shadow-2xl print:shadow-none print:border-0">
          <CardContent className="p-0">
            {/* Header Section */}
            <div className="bg-gradient-to-r from-slate-900 to-slate-800 p-8 md:p-12 border-b-4 border-lime-500 print:border-b-2">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">
                Gediminas Paulauskas
              </h1>
              <p className="text-xl md:text-2xl text-lime-400 mb-6 font-semibold">
                Python Developer
              </p>

              {/* Contact Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-slate-300">
                <div className="flex items-center gap-2 hover:text-lime-400 transition-colors">
                  <MapPin className="w-4 h-4 text-lime-500" />
                  <span>Vilnius, Lithuania</span>
                </div>
                <div className="flex items-center gap-2 hover:text-lime-400 transition-colors">
                  <Linkedin className="w-4 h-4 text-lime-500" />
                  <a href="https://www.linkedin.com/in/menesis" target="_blank" rel="noopener noreferrer">linkedin.com/in/menesis</a>
                </div>
                <div className="flex items-center gap-2 hover:text-lime-400 transition-colors">
                  <Github className="w-4 h-4 text-lime-500" />
                  <a href="https://github.com/menesis" target="_blank" rel="noopener noreferrer">github.com/menesis</a>
                </div>
                <div className="flex items-center gap-2 hover:text-lime-400 transition-colors">
                  <Globe className="w-4 h-4 text-lime-500" />
                  <a href="https://launchpad.net/~menesis" target="_blank" rel="noopener noreferrer">launchpad.net/~menesis</a>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="p-8 md:p-12 space-y-8">
              {/* Summary Section */}
              <section>
                <h2 className="text-2xl font-bold text-lime-400 mb-4 flex items-center gap-2">
                  Professional Summary
                </h2>
                <p className="text-slate-300 leading-relaxed">
                  Full-stack Python developer with 17+ years of work history, mostly developing web apps.
                </p>
              </section>

              <Separator className="bg-slate-700" />

              {/* Top Skills Section */}
              <section>
                <h2 className="text-2xl font-bold text-lime-400 mb-4">Top Skills</h2>
                <div className="flex flex-wrap gap-2">
                  {['Python', 'Django', 'SQL', 'Ubuntu'].map((skill) => (
                    <Badge key={skill} className="bg-lime-600 hover:bg-lime-700 text-white px-4 py-2 text-sm">
                      {skill}
                    </Badge>
                  ))}
                </div>
              </section>

              <Separator className="bg-slate-700" />

              {/* Skills Section */}
              <section>
                <h2 className="text-2xl font-bold text-lime-400 mb-4">Skills</h2>
                <div className="space-y-3 text-slate-300">
                  <div className="leading-relaxed">
                    <span className="text-lime-400 font-semibold">•</span> Extensive Python development experience (13+ years)
                  </div>
                  <div className="leading-relaxed">
                    <span className="text-lime-400 font-semibold">•</span> Extensive experience with relational databases, writing and optimizing SQL, ORM: Oracle, PostgreSQL, MySQL, SQL Server
                  </div>
                  <div className="leading-relaxed">
                    <span className="text-lime-400 font-semibold">•</span> Very good knowledge of web frameworks: Django (5 years), Zope 3 (6 years)
                  </div>
                  <div className="leading-relaxed">
                    <span className="text-lime-400 font-semibold">•</span> Frontend: HTML, CSS, JavaScript, jQuery, Bootstrap
                  </div>
                  <div className="leading-relaxed">
                    <span className="text-lime-400 font-semibold">•</span> Reading/writing of PDF, XLS, CSV, XML documents, XML or JSON APIs
                  </div>
                  <div className="leading-relaxed">
                    <span className="text-lime-400 font-semibold">•</span> GNU/Linux user for 20+ years on desktop and/or server: Debian, Ubuntu
                  </div>
                  <div className="leading-relaxed">
                    <span className="text-lime-400 font-semibold">•</span> Packaging, release and maintenance of Python and Debian packages
                  </div>
                  <div className="leading-relaxed">
                    <span className="text-lime-400 font-semibold">•</span> Advanced user of version control systems: Git, Bazaar, Subversion
                  </div>
                  <div className="leading-relaxed">
                    <span className="text-lime-400 font-semibold">•</span> Test-driven development, coverage: py.test, Selenium, etc.
                  </div>
                  <div className="leading-relaxed">
                    <span className="text-lime-400 font-semibold">•</span> Reviewing code, CI using Jenkins, GitHub, GitLab, Azure DevOps
                  </div>
                  <div className="leading-relaxed">
                    <span className="text-lime-400 font-semibold">•</span> Early adopter of Agile methodology, Scrum, Kanban, Extreme programming
                  </div>
                  <div className="leading-relaxed">
                    <span className="text-lime-400 font-semibold">•</span> Some C#, ASP.NET, PowerShell experience in recent jobs (~2 years)
                  </div>
                  <div className="leading-relaxed">
                    <span className="text-lime-400 font-semibold">•</span> Azure Fundamentals certificate
                  </div>
                  <div className="leading-relaxed">
                    <span className="text-lime-400 font-semibold">•</span> Docker basics
                  </div>
                </div>
              </section>

              <Separator className="bg-slate-700" />

              {/* Experience Section */}
              <section>
                <h2 className="text-2xl font-bold text-lime-400 mb-6">Experience</h2>
                <div className="space-y-6">
                  {/* IBM */}
                  <div className="border-l-2 border-lime-500 pl-6 hover:border-lime-400 transition-colors">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-2">
                      <div>
                        <h3 className="text-xl font-bold text-white">Application Developer</h3>
                        <p className="text-lime-400 font-semibold">IBM</p>
                      </div>
                      <div className="flex items-center gap-2 text-slate-400 mt-1 md:mt-0">
                        <Calendar className="w-4 h-4" />
                        <span className="text-sm">May 2025 - August 2025 (4 months)</span>
                      </div>
                    </div>
                    <p className="text-slate-400 text-sm mb-2 flex items-center gap-2">
                      <MapPin className="w-3 h-3" />
                      Vilnius, Lithuania
                    </p>
                    <p className="text-slate-300 leading-relaxed">
                      Doing Python part in AI agents development team. Technologies: CrewAI, MCP, Pydantic
                    </p>
                  </div>

                  {/* Devoteam */}
                  <div className="border-l-2 border-lime-500 pl-6 hover:border-lime-400 transition-colors">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-2">
                      <div>
                        <h3 className="text-xl font-bold text-white">Senior Python Developer</h3>
                        <p className="text-lime-400 font-semibold">Devoteam</p>
                      </div>
                      <div className="flex items-center gap-2 text-slate-400 mt-1 md:mt-0">
                        <Calendar className="w-4 h-4" />
                        <span className="text-sm">March 2021 - June 2022 (1 year 4 months)</span>
                      </div>
                    </div>
                    <p className="text-slate-400 text-sm mb-2 flex items-center gap-2">
                      <MapPin className="w-3 h-3" />
                      Vilnius, Lithuania
                    </p>
                    <p className="text-slate-300 leading-relaxed">
                      Developed a scanner of on-premises infrastructure for OpsAI.com. Created agents, a server and command line interface from scratch, implemented plugin system and their repository, communication using gRPC. Sadly, the project was cancelled when CLOUDEON was acquired by Devoteam.
                      <br /><br />
                      Learned a lot about Azure. Taught teammates on how to use Git.
                      <br /><br />
                      Worked in Cloud Platform team, implemented Functions or microservices using Python or PowerShell.
                    </p>
                  </div>

                  {/* Barclays */}
                  <div className="border-l-2 border-lime-500 pl-6 hover:border-lime-400 transition-colors">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-2">
                      <div>
                        <h3 className="text-xl font-bold text-white">Hosting Service Delivery Automation</h3>
                        <p className="text-lime-400 font-semibold">Barclays Group Operations Lithuania</p>
                      </div>
                      <div className="flex items-center gap-2 text-slate-400 mt-1 md:mt-0">
                        <Calendar className="w-4 h-4" />
                        <span className="text-sm">August 2017 - August 2019 (2 years 1 month)</span>
                      </div>
                    </div>
                    <p className="text-slate-400 text-sm mb-2 flex items-center gap-2">
                      <MapPin className="w-3 h-3" />
                      Lithuania
                    </p>
                    <p className="text-slate-300 leading-relaxed">
                      Worked across Windows Server, UNIX, and Storage Operations teams automating the work that support engineers were doing.
                      <br /><br />
                      Created a Python/Django app to move users' data when they relocate to another city. The part that reads/writes to Active Directory had to be implemented in PowerShell.
                      <br /><br />
                      Learned C# language, .NET fundamentals, and Visual Studio IDE.
                      <br /><br />
                      Created a few web apps using ASP.NET that called various internal or Barclays-specific APIs to manage incidents, change requests and tasks, check server health, disk space, backup status, and so on.
                      <br /><br />
                      Mostly worked alone on my projects. Since I was in a team of young devops, I had to help them learn programming concepts, tools, solve problems, write better code, but I didn't mentor or manage anyone.
                    </p>
                  </div>

                  {/* POV */}
                  <div className="border-l-2 border-lime-500 pl-6 hover:border-lime-400 transition-colors">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-2">
                      <div>
                        <h3 className="text-xl font-bold text-white">Software Engineer</h3>
                        <p className="text-lime-400 font-semibold">POV</p>
                      </div>
                      <div className="flex items-center gap-2 text-slate-400 mt-1 md:mt-0">
                        <Calendar className="w-4 h-4" />
                        <span className="text-sm">August 2007 - February 2017 (9 years 7 months)</span>
                      </div>
                    </div>
                    <p className="text-slate-400 text-sm mb-2 flex items-center gap-2">
                      <MapPin className="w-3 h-3" />
                      Vilnius
                    </p>
                    <p className="text-slate-300 leading-relaxed">
                      Developed and maintained web applications written in Python, using Zope and Django frameworks. Projects I worked on: payment terminal, laboratory information management system, student information system SchoolTool, 360 degree feedback and personal development system Ivija, and more.
                      <br /><br />
                      Everything was developed and deployed on Linux (Ubuntu). Most applications generate PDF reports, import/export Excel, send emails, but otherwise are standalone. Everything has reproducible builds and good test coverage ensured by continuous integration. Projects are managed in short iterations and delivered often according to Agile methodology.
                      <br /><br />
                      Besides development, I was responsible for Debian packaging, made releases, maintained translations, mentored new team members, reviewed code, responded to customer inquiries.
                      <br /><br />
                      Technologies: Python, Zope, Django, Debian, PostgreSQL, Reportgen, Buildout, jQuery, Git, Bazaar.
                    </p>
                  </div>

                  {/* Idega */}
                  <div className="border-l-2 border-lime-500 pl-6 hover:border-lime-400 transition-colors">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-2">
                      <div>
                        <h3 className="text-xl font-bold text-white">Web Developer</h3>
                        <p className="text-lime-400 font-semibold">Idega</p>
                      </div>
                      <div className="flex items-center gap-2 text-slate-400 mt-1 md:mt-0">
                        <Calendar className="w-4 h-4" />
                        <span className="text-sm">August 2006 - May 2007 (10 months)</span>
                      </div>
                    </div>
                    <p className="text-slate-400 text-sm mb-2 flex items-center gap-2">
                      <MapPin className="w-3 h-3" />
                      Reykjavik, Iceland
                    </p>
                    <p className="text-slate-300 leading-relaxed">
                      Developed and fixed Java-based custom web application framework, used at many Icelandic muncipalities, sports clubs and travel agencies.
                      <br /><br />
                      Used technologies: XForms, JSF, EJB 2, WebDAV, and XML, HTML, CSS, etc.
                      <br /><br />
                      Application servers: Tomcat.
                      <br /><br />
                      Tools: Eclipse, Maven, CVS.
                      <br /><br />
                      Operating systems: Mac OS X, Linux.
                      <br /><br />
                      First 4 months worked in Reykjavik, Iceland; later with a small team in Vilnius, Lithuania.
                    </p>
                  </div>

                  {/* TietoEnator */}
                  <div className="border-l-2 border-lime-500 pl-6 hover:border-lime-400 transition-colors">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-2">
                      <div>
                        <h3 className="text-xl font-bold text-white">Systems Analyst</h3>
                        <p className="text-lime-400 font-semibold">TietoEnator Consulting</p>
                      </div>
                      <div className="flex items-center gap-2 text-slate-400 mt-1 md:mt-0">
                        <Calendar className="w-4 h-4" />
                        <span className="text-sm">October 2005 - July 2006 (10 months)</span>
                      </div>
                    </div>
                    <p className="text-slate-300 leading-relaxed">
                      Developed and maintained parts of Network Information System for TEO.LT, major Lithuanian telecom and ISP.
                      <br /><br />
                      My job was programming and optimizing database queries and procedures -- in Transact-SQL, MS SQL Server. Used mostly Microsoft tools supplied with the server, and Visual Studio.
                    </p>
                  </div>

                  {/* Alna */}
                  <div className="border-l-2 border-lime-500 pl-6 hover:border-lime-400 transition-colors">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-2">
                      <div>
                        <h3 className="text-xl font-bold text-white">Programmer</h3>
                        <p className="text-lime-400 font-semibold">Alna</p>
                      </div>
                      <div className="flex items-center gap-2 text-slate-400 mt-1 md:mt-0">
                        <Calendar className="w-4 h-4" />
                        <span className="text-sm">September 2001 - February 2004 (2 years 6 months)</span>
                      </div>
                    </div>
                    <p className="text-slate-300 leading-relaxed">
                      Design, development and maintenance of business solutions for mobile networks operators (mostly Bite Lietuva) # customer management at sales outlets, customer self care, automation of changes to massive amounts of customers, invoice generation and printing.
                      <br /><br />
                      Programming languages: Java, XML (including XSL, XSL-FO), HTML, CSS, and Javascript, PL/SQL; some C++.
                      <br /><br />
                      Technologies: J2EE (JSP, Servlets, EJB, JAXP, JMS, JAAS), Apache Struts.
                      <br /><br />
                      Operating systems and servers: Windows, Linux, Tru64 Unix; JBoss, Borland Application Server, Tomcat, Oracle Database.
                      <br /><br />
                      Tools: Eclipse, Apache Ant, CVS, Bugzilla, PL/SQL Developer, JUnit.
                    </p>
                  </div>
                </div>
              </section>

              <Separator className="bg-slate-700" />

              {/* Education Section */}
              <section>
                <h2 className="text-2xl font-bold text-lime-400 mb-4">Education</h2>
                <div className="border-l-2 border-lime-500 pl-6">
                  <h3 className="text-xl font-bold text-white">Kaunas University of Technology</h3>
                  <p className="text-slate-300">Computer Science</p>
                  <p className="text-slate-400 text-sm flex items-center gap-2 mt-1">
                    <Calendar className="w-4 h-4" />
                    1999 - 2001
                  </p>
                </div>
              </section>

              <Separator className="bg-slate-700" />

              {/* Languages Section */}
              <section>
                <h2 className="text-2xl font-bold text-lime-400 mb-4">Languages</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-slate-300">
                  <div className="flex items-center justify-between p-3 bg-slate-900 rounded-lg">
                    <span>Lithuanian</span>
                    <Badge className="bg-lime-600">Native</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-900 rounded-lg">
                    <span>English</span>
                    <Badge className="bg-lime-600">Professional</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-900 rounded-lg">
                    <span>German</span>
                    <Badge className="bg-slate-700">Elementary</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-900 rounded-lg">
                    <span>Russian</span>
                    <Badge className="bg-slate-700">Elementary</Badge>
                  </div>
                </div>
              </section>

              <Separator className="bg-slate-700" />

              {/* Certifications Section */}
              <section>
                <h2 className="text-2xl font-bold text-lime-400 mb-4 flex items-center gap-2">
                  <Award className="w-6 h-6" />
                  Certifications
                </h2>
                <div className="border-l-2 border-lime-500 pl-6">
                  <p className="text-slate-300 flex items-start gap-2">
                    <span className="text-lime-400 font-semibold">•</span>
                    Microsoft Certified: Azure Fundamentals
                  </p>
                </div>
              </section>
            </div>

            {/* Footer */}
            <div className="bg-slate-900 p-6 text-center text-slate-400 text-sm border-t border-slate-700">
              <p>© 2025 Gediminas Paulauskas. All rights reserved.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Resume;